import React, { useState, useRef, useEffect, useMemo } from "react";
import { SearchInput } from "components/input";
import axios from "axios";
import { VendorItemDetails } from "types/vendor";
import { toast } from "react-toastify";
import { useQuery, useQueryClient } from "react-query";

import { getInventoryCategories, getInventoryItems, getSiteoneInventoryCategories, getSiteoneInventoryItems, saveSiteoneLinkedItem, changeSiteoneItemLink, unlinkSiteoneItemLink } from "api/inventoryApi";
import { TInventory, TInventoryCategory } from "../../../../../types/inventory";
import Spinner from "components/spinner";

import styles from "./VendorItemLinkModalBody.module.scss";
import { Button } from "components/button";
import { Input } from "components/input";
import { useTranslation } from 'react-i18next';
import { useSnackbarContext } from "components/snackbar";
import NoProductImage from "../../../../../images/placeholders/no-product.jpg";
import VendorLeadTimeInput from "views/vendors/components/VendorLeadTimeInput";
import VendorItemNotesInput from "views/vendors/components/VendorItemNotesInput";
import VendorItemPriceInput from "views/vendors/components/VendorItemPriceInput";
import { formatCentsToVendorPriceInput } from "views/vendors/constants/vendorItemPrice";
import { clampVendorItemNotes } from "views/vendors/constants/vendorItemFields";
import { normalizeLeadTimeStored } from "views/vendors/constants/vendorLeadTime";
import {
	buildHeritageSectionedList,
	localItemHasNoHeritageCounterpart,
} from "views/vendors/utils/heritageLinkSections";
import { createAndLinkHeritagePart } from "views/vendors/utils/createAndLinkHeritagePart";
import ClearableSearchInput from "./ClearableSearchInput";
import HeritageItemPurchaseHistoryModal from "./HeritageItemPurchaseHistoryModal";
import HeritageLinkConfirmModal, {
	HeritageLinkConfirmRequest,
} from "./HeritageLinkConfirmModal";
import HeritageUnlinkConfirmModal, {
	HeritageUnlinkConfirmRequest,
} from "./HeritageUnlinkConfirmModal";
import useDebounce from "hooks/useDebounce";
import Select from "components/react-select";


interface Props {
	vendorId: number | undefined;
}

function getGreenShade(score: number, maxScore: number): string {
	// Range from light to dark green
	const percent = score / maxScore;
	const light = 240; // lightest green value
	const dark = 80;   // darkest green value

	const greenValue = Math.floor(light - (light - dark) * percent);
	return `rgb(${greenValue}, 255, ${greenValue})`;
}

function normalizeAndTokenize(str: string): string[] {
	return str
		.toLowerCase()
		.replace(/[^a-z0-9\s]/gi, "")  // keep spaces to preserve word boundaries
		.split(/\s+/)                 // split on whitespace
		.filter(Boolean);            // remove empty strings
}

function formatListPriceForInput(listPrice: number | null | undefined): string {
	return formatCentsToVendorPriceInput(listPrice);
}

function getItemDisplayName(item: any): string {
	// Some inventory rows may have `item_name` missing; fall back to other text fields
	// so the list doesn't appear empty on initial load.
	const longDesc =
		typeof item?.long_description === "string" ? item.long_description.slice(0, 80) : "";
	return (
		item?.vendor_part_number ||
		item?.item_name ||
		item?.short_description ||
		longDesc ||
		item?.model ||
		item?.sku ||
		(item?.item_id != null ? `Item ${item.item_id}` : null) ||
		"Untitled"
	);
}

function getHeritageSearchQueryForLocalItem(item: VendorItemDetails | undefined): string {
	if (!item) return "";
	return (
		item.vendor_part_number ||
		item.model ||
		item.sku ||
		item.item_name ||
		""
	);
}

function getLocalSearchQueryForLocalItem(item: VendorItemDetails | undefined): string {
	if (!item) return "";
	return (
		item.item_name ||
		item.model ||
		item.sku ||
		item.vendor_part_number ||
		""
	);
}


const VendorItemLinkModalBody = ({ vendorId, isSiteoneVendor, isHeritageVendor }: any) => {
	const { t } = useTranslation();
	const tBase = "views.vendors.VendorBody.VendorItems.VendorItemLinkModal.VendorItemLinkModalBody";
	const isIntegratedVendor = Boolean(isSiteoneVendor || isHeritageVendor);
	const tr = (key: string) => t(`${tBase}.${key}`);
	const trAction = (linkKey: string, addKey: string) =>
		tr(isIntegratedVendor ? linkKey : addKey);

	const { showSnackbar } = useSnackbarContext();
	const queryClient = useQueryClient();

	const [selectedCategory, setSelectedCategory] = useState<TInventoryCategory | null>(null);
	const [siteoneSelectedCategory, setSiteoneSelectedCategory] = useState<TInventoryCategory | null>(null);
	const [selectedItem, setSelectedItem] = useState<TInventory | null>(null);
	const [selectedHeritageItemId, setSelectedHeritageItemId] = useState<number | null>(null);
	const [selectedHeritagePartNumber, setSelectedHeritagePartNumber] = useState<string | null>(null);
	const [selectedSiteoneItem, setSelectedSiteoneItem] = useState<TInventory | null>(null);
	const [price, setPrice] = useState<string>("");
	const [leadTime, setLeadTime] = useState<string>("");
	const [notes, setNotes] = useState<string>("");
	const [recommendedSiteoneItems, setRecommendedSiteoneItems] = useState<{ item: TInventory; score: number }[]>([]);
	const [recommendedSiteoneCategory, setRecommendedSiteoneCategory] = useState<TInventoryCategory | null>(null);
	const [isSearchingLinkedSiteoneItem, setIsSearchingLinkedSiteoneItem] = useState(false);
	const [isSearchingLinkedLocalItem, setIsSearchingLinkedLocalItem] = useState(false);

	const recommendedItemRef = useRef<HTMLDivElement | null>(null);
	const pendingHeritageScrollPart = useRef<string | null>(null);
	const pendingLocalScrollItemId = useRef<number | null>(null);
	const [recommendedIndex, setRecommendedIndex] = useState(0);
	const currentRecommendedItem = recommendedSiteoneItems[recommendedIndex]?.item || null;

	const [userItems, setUserItems] = useState<VendorItemDetails[]>([]);
	const [search, setSearch] = useState("");
	const [heritageResults, setHeritageResults] = useState<any[]>([]);
	const [heritagePurchasedOnly, setHeritagePurchasedOnly] = useState(false);
	// Search Heritage's own catalog rather than the copy downloaded here. The local
	// copy only holds what has already been downloaded, so a part the customer has
	// never bought cannot be found in it — which is the reason to search Heritage.
	const [heritageSearchAllOfHeritage, setHeritageSearchAllOfHeritage] = useState(false);
	const [heritageItemsLoading, setHeritageItemsLoading] = useState(false);
	const [heritageItemsError, setHeritageItemsError] = useState<string | null>(null);
	/** Branches the last remote search covered (#32); null outside remote mode. */
	const [searchedBranches, setSearchedBranches] = useState<
		{ branch_code: string | null; branch_name: string | null }[] | null
	>(null);
	const [searchBranchErrors, setSearchBranchErrors] = useState<
		{ branch_code: string | null; message: string }[]
	>([]);
	/**
	 * Live per-branch stock for the rendered results (#33), keyed by part
	 * number. Loaded asynchronously after the results render: getInventory
	 * answers in seconds, and the search must not wait for it.
	 */
	const [branchStock, setBranchStock] = useState<
		Record<string, { branch_code: string; qty: number | null; price: number | null }[]>
	>({});
	const [branchStockLoading, setBranchStockLoading] = useState(false);
	const [localSearch, setLocalSearch] = useState("");
	const [heritageHistoryItem, setHeritageHistoryItem] = useState<{
		heritage_part_number: string;
		description?: string | null;
	} | null>(null);
	const [linkConfirmRequest, setLinkConfirmRequest] =
		useState<HeritageLinkConfirmRequest | null>(null);
	const [unlinkConfirmRequest, setUnlinkConfirmRequest] =
		useState<HeritageUnlinkConfirmRequest | null>(null);
	const [isHeritageLinkSubmitting, setIsHeritageLinkSubmitting] = useState(false);
	const [isHeritageUnlinkSubmitting, setIsHeritageUnlinkSubmitting] = useState(false);

	const [siteoneSiteoneItemSearch, setSiteoneSiteoneItemSearch] = useState("");
	const [siteoneSiteoneItemNumberSearch, setSiteoneSiteoneItemNumberSearch] = useState("");
	const [siteoneLocalItemSearch, setSiteoneLocalItemSearch] = useState("");
	const [siteoneLocalItemNumberSearch, setSiteoneLocalItemNumberSearch] = useState("");
	const [siteoneItemNumberSearch, setSiteoneItemNumberSearch] = useState(false);
	const [localItemNumberSearch, setLocalItemNumberSearch] = useState(false);
	const [linkItemSearch, setLinkItemSearch] = useState("");
	const debouncedLocalSearch = useDebounce(localSearch, 300);
	const debouncedSearch = useDebounce(search, 300);
	const debouncedLinkItemSearch = useDebounce(linkItemSearch, 300);

	// #22 — parts currently being added-and-linked, keyed by heritage part
	// number, so each row can disable its own button without blocking the rest.
	const [addingHeritageParts, setAddingHeritageParts] = useState<Set<string>>(new Set());

	// #23 — narrow the Heritage pane to linked / unlinked rows, and multi-select
	// add-eligible rows for one bulk "Add and link".
	const [heritageLinkFilter, setHeritageLinkFilter] = useState<"all" | "linked" | "unlinked">("all");
	const [bulkSelectedParts, setBulkSelectedParts] = useState<Set<string>>(new Set());
	const [isBulkAdding, setIsBulkAdding] = useState(false);

	/**
	 * #22 "Add and link": create the local inventory item for a Heritage part the
	 * company does not carry, linked to this vendor in the same call. On success
	 * the local pane is refetched, which flips the row to linked (green) and
	 * moves it into "In your items".
	 */
	const handleAddAndLink = async (
		part: { heritage_part_number: string; description?: string | null },
		e: React.MouseEvent
	) => {
		e.stopPropagation();
		const partNumber = part.heritage_part_number;
		if (addingHeritageParts.has(partNumber)) {
			return;
		}
		setAddingHeritageParts((prev) => new Set(prev).add(partNumber));
		try {
			const createdItemId = await createAndLinkHeritagePart(vendorId, part);
			// The row's linked state is the SERVER's flag since #20 — stamp it
			// locally so the row flips without refetching the heritage search.
			setHeritageResults((prev) =>
				prev.map((row) =>
					row.heritage_part_number === partNumber
						? { ...row, linked_item_id: createdItemId ?? -1 }
						: row
				)
			);
			toast.success(tr("Added and linked").replace("{{part}}", partNumber), {
				autoClose: 2500,
			});
			const endpoint = `${process.env.REACT_APP_SERVER_URL}/api/v1/company/heritage/local-items/search`;
			const params: Record<string, string | number> =
				localSearch.trim().length >= 2 ? { q: localSearch, vendorId } : { vendorId };
			const res = await axios.get(endpoint, { params });
			setUserItems(res.data.items || []);
		} catch (error) {
			const serverMessage = axios.isAxiosError(error)
				? error.response?.data?.message
				: null;
			toast.error(serverMessage || tr("Add and link failed"));
		} finally {
			setAddingHeritageParts((prev) => {
				const next = new Set(prev);
				next.delete(partNumber);
				return next;
			});
		}
	};

	// #20's server flag decides linked-ness; the client check remains only for
	// an older API without the field. Shared by sections, the filter and bulk add.
	const isHeritageRowLinked = (candidate: {
		heritage_part_number: string;
		linked_item_id?: number | null;
	}) =>
		candidate.linked_item_id !== undefined
			? candidate.linked_item_id != null
			: userItems.some((ui) => ui.vendor_part_number === candidate.heritage_part_number);

	const filteredHeritageResults =
		heritageLinkFilter === "all"
			? heritageResults
			: heritageResults.filter((candidate) =>
					heritageLinkFilter === "linked"
						? isHeritageRowLinked(candidate)
						: !isHeritageRowLinked(candidate)
				);

	/**
	 * #23 — one "Add and link" over every selected part. Sequential on purpose
	 * (the endpoint creates inventory rows; parallel writes would race the
	 * duplicate guards), ONE local-pane refetch at the end, one summary. Failed
	 * parts stay selected so a retry is one click.
	 */
	const handleBulkAddAndLink = async () => {
		if (isBulkAdding || bulkSelectedParts.size === 0) return;
		setIsBulkAdding(true);
		const parts = heritageResults.filter(
			(item) =>
				bulkSelectedParts.has(item.heritage_part_number) &&
				!isHeritageRowLinked(item) &&
				item.matched_item_id == null
		);
		const failures: Array<{ part: string; message: string }> = [];
		let added = 0;
		for (const part of parts) {
			try {
				const createdItemId = await createAndLinkHeritagePart(vendorId, part);
				setHeritageResults((prev) =>
					prev.map((row) =>
						row.heritage_part_number === part.heritage_part_number
							? { ...row, linked_item_id: createdItemId ?? -1 }
							: row
					)
				);
				added += 1;
				setBulkSelectedParts((prev) => {
					const next = new Set(prev);
					next.delete(part.heritage_part_number);
					return next;
				});
			} catch (error) {
				const message = axios.isAxiosError(error)
					? error.response?.data?.message || error.message
					: String(error);
				failures.push({ part: part.heritage_part_number, message });
			}
		}
		try {
			const endpoint = `${process.env.REACT_APP_SERVER_URL}/api/v1/company/heritage/local-items/search`;
			const params: Record<string, string | number> =
				localSearch.trim().length >= 2 ? { q: localSearch, vendorId } : { vendorId };
			const res = await axios.get(endpoint, { params });
			setUserItems(res.data.items || []);
		} catch {
			// The adds themselves succeeded; a failed refetch only delays the flip.
		}
		if (added > 0) {
			toast.success(tr("Added and linked items").replace("{{count}}", String(added)), {
				autoClose: 3000,
			});
		}
		for (const failure of failures) {
			toast.error(`${failure.part}: ${failure.message}`);
		}
		setIsBulkAdding(false);
	};

	const heritageSearchOptions = useMemo(
		() => [
			{ value: "catalog_only", label: tr("Downloaded catalog") },
			{ value: "search_all", label: tr("Search all of Heritage") },
			{ value: "purchased_only", label: tr("Previously purchased only") },
		],
		[tr]
	);

	const selectedHeritageSearchOption = useMemo(
		() =>
			heritageSearchOptions.find(
				(opt) =>
					opt.value ===
					(heritagePurchasedOnly
						? "purchased_only"
						: heritageSearchAllOfHeritage
							? "search_all"
							: "catalog_only")
			) || heritageSearchOptions[0],
		[heritagePurchasedOnly, heritageSearchAllOfHeritage, heritageSearchOptions]
	);


	useEffect(() => {
		if (!vendorId) return;
		let cancelled = false;
		const endpoint = `${process.env.REACT_APP_SERVER_URL}/api/v1/company/heritage/local-items/search`;
		const params: Record<string, string | number> =
			debouncedLocalSearch.trim().length >= 2 ? { q: debouncedLocalSearch, vendorId } : { vendorId };
		axios.get(endpoint, { params }).then((res) => {
			if (!cancelled) setUserItems(res.data.items || []);
		});
		return () => {
			cancelled = true;
		};
	}, [vendorId, debouncedLocalSearch]);

	useEffect(() => {
		let cancelled = false;
		const endpoint = `${process.env.REACT_APP_SERVER_URL}/api/v1/company/heritage/heritage-items/search`;
		const params: Record<string, string | number> = {};
		if (debouncedSearch.trim().length >= 2) {
			params.q = debouncedSearch;
		}
		// #20 — every mode names the vendor, so the server can say per row
		// whether the part is actually linked to it (linked_item_id).
		if (vendorId) params.vendorId = vendorId;
		if (heritagePurchasedOnly) {
			params.purchasedOnly = "true";
		}
		if (heritageSearchAllOfHeritage) {
			params.remote = "true";
		}

		setHeritageItemsLoading(true);
		setHeritageItemsError(null);
		axios
			.get(endpoint, { params })
			.then((res) => {
				if (cancelled) return;
				setHeritageResults(res.data.items || []);
				setSearchedBranches(heritageSearchAllOfHeritage ? res.data.branches || null : null);
				setSearchBranchErrors(heritageSearchAllOfHeritage ? res.data.branch_errors || [] : []);
			})
			.catch((error) => {
				if (cancelled) return;
				setHeritageResults([]);
				setSearchedBranches(null);
				setSearchBranchErrors([]);
				const message =
					error?.response?.data?.message ||
					(heritagePurchasedOnly
						? tr("Previously purchased items load failed")
						: tr("Heritage items load failed"));
				setHeritageItemsError(message);
			})
			.finally(() => {
				if (!cancelled) setHeritageItemsLoading(false);
			});
		return () => {
			cancelled = true;
		};
	}, [debouncedSearch, heritagePurchasedOnly, heritageSearchAllOfHeritage, vendorId]);

	useEffect(() => {
		const parts = heritageResults
			.map((item) => item.heritage_part_number)
			.filter(Boolean)
			.slice(0, 25);
		setBranchStock({});
		if (parts.length === 0) {
			setBranchStockLoading(false);
			return;
		}

		let cancelled = false;
		setBranchStockLoading(true);
		axios
			.post(`${process.env.REACT_APP_SERVER_URL}/api/v1/company/heritage/items/branch-stock`, {
				partNumbers: parts,
				vendorId,
			})
			.then((res) => {
				if (cancelled) return;
				const byPart: Record<
					string,
					{ branch_code: string; qty: number | null; price: number | null }[]
				> = {};
				for (const row of res.data?.parts || []) {
					byPart[row.heritage_part_number] = row.stock || [];
				}
				setBranchStock(byPart);
			})
			.catch(() => {
				// The chips are an enhancement over the cached number already on
				// screen; a failed lookup keeps that number rather than nagging.
			})
			.finally(() => {
				if (!cancelled) setBranchStockLoading(false);
			});
		return () => {
			cancelled = true;
		};
	}, [heritageResults, vendorId]);

	const showLinkedHeritageItem = (
		item: VendorItemDetails,
		e: React.MouseEvent<HTMLButtonElement>
	) => {
		e.stopPropagation();
		const partNumber = item.vendor_part_number?.trim();
		if (!partNumber) return;

		if (heritagePurchasedOnly) {
			setHeritagePurchasedOnly(false);
			setHeritageSearchAllOfHeritage(true);
		}

		setSelectedHeritageItemId(item.item_id);
		setSelectedHeritagePartNumber(partNumber);
		setSearch(partNumber);
		pendingHeritageScrollPart.current = partNumber;
	};

	useEffect(() => {
		const partNumber = pendingHeritageScrollPart.current;
		if (!partNumber) return;

		const isLoaded = heritageResults.some(
			(heritageItem) => heritageItem.heritage_part_number === partNumber
		);
		if (!isLoaded) return;

		const el = document.getElementById(`heritage-link-item-${partNumber}`);
		if (el) {
			el.scrollIntoView({ block: "nearest", behavior: "smooth" });
			pendingHeritageScrollPart.current = null;
		}
	}, [heritageResults]);

	const showLinkedLocalItem = (
		heritagePartNumber: string,
		e: React.MouseEvent<HTMLButtonElement>
	) => {
		e.stopPropagation();
		const linkedLocalItem = userItems.find(
			(ui) => ui.vendor_part_number === heritagePartNumber
		);
		if (!linkedLocalItem?.item_id) return;

		setSelectedHeritageItemId(linkedLocalItem.item_id);
		setSelectedHeritagePartNumber(heritagePartNumber);
		const query = getLocalSearchQueryForLocalItem(linkedLocalItem);
		if (query) setLocalSearch(query);
		pendingLocalScrollItemId.current = linkedLocalItem.item_id;
	};

	useEffect(() => {
		const itemId = pendingLocalScrollItemId.current;
		if (itemId == null) return;

		const isLoaded = userItems.some((item) => item.item_id === itemId);
		if (!isLoaded) return;

		const el = document.getElementById(`local-link-item-${itemId}`);
		if (el) {
			el.scrollIntoView({ block: "nearest", behavior: "smooth" });
			pendingLocalScrollItemId.current = null;
		}
	}, [userItems]);

	const requestHeritageLink = (heritagePartNumber: string) => {
		if (selectedHeritageItemId == null || !vendorId) return;

		const localItem = userItems.find((i) => i.item_id === selectedHeritageItemId);
		if (!localItem) return;

		if (localItem.vendor_part_number === heritagePartNumber) {
			toast.info(tr("Already linked to this Heritage item"), { autoClose: 2500 });
			return;
		}

		const heritageMatch = heritageResults.find(
			(item) => item.heritage_part_number === heritagePartNumber
		);
		const existingPartNumber = localItem.vendor_part_number?.trim() || null;
		const existingMatch = existingPartNumber
			? heritageResults.find((item) => item.heritage_part_number === existingPartNumber)
			: null;

		setLinkConfirmRequest({
			localItem,
			heritagePartNumber,
			heritageDescription: heritageMatch?.description ?? null,
			existingPartNumber,
			existingDescription: existingMatch?.description ?? null,
		});
	};

	const confirmHeritageLink = async () => {
		if (!linkConfirmRequest || !vendorId) return;

		setIsHeritageLinkSubmitting(true);
		try {
			await axios.post(`${process.env.REACT_APP_SERVER_URL}/api/v1/company/heritage/link`, {
				itemId: linkConfirmRequest.localItem.item_id,
				vendorId,
				heritagePartNumber: linkConfirmRequest.heritagePartNumber,
			});

			toast.success(
				tr("Heritage link success")
					.replace("{{local}}", linkConfirmRequest.localItem.item_name || "")
					.replace("{{heritage}}", linkConfirmRequest.heritagePartNumber),
				{ autoClose: 2500 }
			);

			const endpoint = `${process.env.REACT_APP_SERVER_URL}/api/v1/company/heritage/local-items/search`;
			const params: Record<string, string | number> =
				localSearch.trim().length >= 2 ? { q: localSearch, vendorId } : { vendorId };
			const res = await axios.get(endpoint, { params });
			setUserItems(res.data.items || []);
			setSelectedHeritagePartNumber(linkConfirmRequest.heritagePartNumber);
			setLinkConfirmRequest(null);
		} catch {
			toast.error(tr("Failed to link Heritage item"));
		} finally {
			setIsHeritageLinkSubmitting(false);
		}
	};

	const handleLink = requestHeritageLink;

	const requestHeritageUnlink = () => {
		if (selectedHeritageItemId == null || !vendorId) return;

		const localItem = userItems.find((i) => i.item_id === selectedHeritageItemId);
		if (!localItem) return;

		const heritagePartNumber = localItem.vendor_part_number?.trim();
		if (!heritagePartNumber) {
			toast.info(tr("No Heritage link to remove"), { autoClose: 2500 });
			return;
		}

		const existingMatch = heritageResults.find(
			(item) => item.heritage_part_number === heritagePartNumber
		);

		setUnlinkConfirmRequest({
			localItem,
			heritagePartNumber,
			heritageDescription: existingMatch?.description ?? null,
		});
	};

	const confirmHeritageUnlink = async () => {
		if (!unlinkConfirmRequest || !vendorId) return;

		setIsHeritageUnlinkSubmitting(true);
		try {
			await axios.post(`${process.env.REACT_APP_SERVER_URL}/api/v1/company/heritage/unlink`, {
				itemId: unlinkConfirmRequest.localItem.item_id,
				vendorId,
			});

			toast.success(
				tr("Heritage unlink success").replace(
					"{{local}}",
					unlinkConfirmRequest.localItem.item_name || ""
				),
				{ autoClose: 2500 }
			);

			const endpoint = `${process.env.REACT_APP_SERVER_URL}/api/v1/company/heritage/local-items/search`;
			const params: Record<string, string | number> =
				localSearch.trim().length >= 2 ? { q: localSearch, vendorId } : { vendorId };
			const res = await axios.get(endpoint, { params });
			setUserItems(res.data.items || []);
			setSelectedHeritagePartNumber(null);
			setUnlinkConfirmRequest(null);
		} catch {
			toast.error(tr("Failed to unlink Heritage item"));
		} finally {
			setIsHeritageUnlinkSubmitting(false);
		}
	};

	const handleUnlink = requestHeritageUnlink;

	const categoryQuery = useQuery(["categories"], getInventoryCategories);

	// Search query — used when a search string is entered
	const allItemsBySearch = useQuery(
		["vendor_link_items_search", siteoneLocalItemSearch, siteoneLocalItemNumberSearch],
		() => getInventoryItems(null, siteoneLocalItemSearch, siteoneLocalItemNumberSearch),
		{
			enabled: !!siteoneLocalItemSearch?.trim() || !!siteoneLocalItemNumberSearch?.trim(), // only when search is active
		}
	);

	// Category-only query — used when there's no search
	const categoryItemsQuery = useQuery(
		["vendor_link_items_by_category", selectedCategory?.category_id],
		() => getInventoryItems(selectedCategory?.category_id || null, null, null),
		{
			enabled: !siteoneLocalItemSearch?.trim() && !siteoneLocalItemNumberSearch?.trim() && !!selectedCategory,
		}
	);

	const activeItems = siteoneLocalItemSearch?.trim() || siteoneLocalItemNumberSearch?.trim()
		? allItemsBySearch.data || []
		: categoryItemsQuery.data || [];


	const filteredCategories = useMemo(() => {
		const categories = categoryQuery.data || [];

		// If no search, return all
		if (!allItemsBySearch.data || (!siteoneLocalItemSearch?.trim() && !siteoneLocalItemNumberSearch?.trim())) {
			return categories;
		}

		const matchedCategoryIds = new Set(
			allItemsBySearch.data.flatMap((item: TInventory) => [
				item.root_id,
				item.cat_id_v2,
				item.category_id,
			])
		);

		return categories.filter((cat: any) =>
			matchedCategoryIds.has(cat.category_id) ||
			matchedCategoryIds.has(cat.root_id) ||
			matchedCategoryIds.has(cat.cat_id_v2)
		);
	}, [categoryQuery.data, allItemsBySearch.data, siteoneLocalItemSearch, siteoneLocalItemNumberSearch]);



	const filteredItems = useMemo(() => {
		if (!selectedCategory || !activeItems) return [];

		return activeItems.filter((item: TInventory) => {
			const catId = selectedCategory.category_id;
			return (
				item.root_id === catId ||
				item.cat_id_v2 === catId ||
				item.category_id === catId
			);
		})
	}, [activeItems, selectedCategory, selectedItem]);

	// Default vendor link flow — search filters categories and items together
	const linkItemsBySearch = useQuery(
		["vendor_link_items_search_default", debouncedLinkItemSearch],
		() => getInventoryItems(null, debouncedLinkItemSearch, null),
		{
			enabled: !!debouncedLinkItemSearch?.trim(),
		}
	);

	const linkCategoryItemsQuery = useQuery(
		["vendor_link_items_by_category_default", selectedCategory?.category_id],
		() => getInventoryItems(selectedCategory?.category_id || null, null, null),
		{
			enabled: !debouncedLinkItemSearch?.trim() && !!selectedCategory,
		}
	);

	const linkActiveItems = debouncedLinkItemSearch?.trim()
		? linkItemsBySearch.data || []
		: linkCategoryItemsQuery.data || [];

	const linkFilteredCategories = useMemo(() => {
		const categories = categoryQuery.data || [];

		if (!linkItemsBySearch.data || !debouncedLinkItemSearch?.trim()) {
			return categories;
		}

		const matchedCategoryIds = new Set(
			linkItemsBySearch.data.flatMap((item: TInventory) => [
				item.root_id,
				item.cat_id_v2,
				item.category_id,
			])
		);

		return categories.filter((cat: any) =>
			matchedCategoryIds.has(cat.category_id) ||
			matchedCategoryIds.has(cat.root_id) ||
			matchedCategoryIds.has(cat.cat_id_v2)
		);
	}, [categoryQuery.data, linkItemsBySearch.data, debouncedLinkItemSearch]);

	const linkFilteredItems = useMemo(() => {
		if (!selectedCategory || !linkActiveItems) return [];

		return linkActiveItems.filter((item: TInventory) => {
			const catId = selectedCategory.category_id;
			return (
				item.root_id === catId ||
				item.cat_id_v2 === catId ||
				item.category_id === catId
			);
		});
	}, [linkActiveItems, selectedCategory]);

	useEffect(() => {
		if (!linkItemSearch?.trim() || linkFilteredCategories.length === 0) return;

		setSelectedCategory((prev) => {
			if (prev && linkFilteredCategories.some((c) => c.category_id === prev.category_id)) {
				return prev;
			}
			return linkFilteredCategories[0];
		});
	}, [linkFilteredCategories, linkItemSearch]);

	useEffect(() => {
		if (!linkItemSearch?.trim() || !selectedItem) return;

		const stillVisible = linkFilteredItems.some(
			(i) => String(i.item_id) === String(selectedItem.item_id)
		);
		if (!stillVisible) {
			setSelectedItem(null);
			setPrice("");
		}
	}, [linkFilteredItems, linkItemSearch, selectedItem]);

	useEffect(() => {
		if (activeItems.length > 0 && filteredCategories.length > 0) {
			setSelectedCategory(filteredCategories[0]);
		}
	}, [filteredCategories]);


	const siteoneCategoryQuery = useQuery(["siteone_categories"], getSiteoneInventoryCategories);
	const allSiteoneItemsQuery = useQuery(
		["siteone_vendor_link_items_all", siteoneSiteoneItemSearch, siteoneSiteoneItemNumberSearch],
		async () => {
			// null categoryId so it doesn't filter by category server-side
			return getSiteoneInventoryItems(null, siteoneSiteoneItemSearch, siteoneSiteoneItemNumberSearch);
		},
		{
			enabled: !!siteoneSiteoneItemSearch?.trim() || !!siteoneSiteoneItemNumberSearch?.trim(), // only when search is active
		}
	);

	const siteoneCategoryItemsQuery = useQuery(
		["siteone_vendor_link_items_category", siteoneSelectedCategory?.category_id],
		async () => {
			return getSiteoneInventoryItems(siteoneSelectedCategory?.category_id ?? null, null, null);
		},
		{
			enabled: !siteoneSiteoneItemSearch?.trim() || !siteoneSiteoneItemNumberSearch?.trim() || !!siteoneSelectedCategory,
		}
	);


	// if no string search is active, get all siteone items
	const activeSiteoneItems = siteoneSiteoneItemSearch?.trim() || siteoneSiteoneItemNumberSearch?.trim()
		? allSiteoneItemsQuery.data || []
		: siteoneCategoryItemsQuery.data || [];


	const filteredSiteoneCategories = useMemo(() => {
		if (siteoneCategoryQuery.isLoading || !siteoneCategoryQuery.data) return [];

		const categories = siteoneCategoryQuery.data.filter((cat: any) => cat.category_id != null);

		if (!allSiteoneItemsQuery.data || (!siteoneSiteoneItemSearch?.trim() && !siteoneSiteoneItemNumberSearch?.trim())) {
			return categories;
		}

		const matchedCategoryIds = new Set(
			allSiteoneItemsQuery.data.map((item: TInventory) => item.line)
		);

		return categories.filter((cat: any) =>
			matchedCategoryIds.has(cat.category_id)
		);
	}, [siteoneCategoryQuery.data, allSiteoneItemsQuery.data, siteoneSiteoneItemSearch, siteoneSiteoneItemNumberSearch]);


	const filteredSiteoneItems = useMemo(() => {
		if (!activeSiteoneItems || !siteoneSelectedCategory) return [];

		return activeSiteoneItems.filter((item: TInventory) =>
			item.line === siteoneSelectedCategory.category_id
		);
	}, [activeSiteoneItems, siteoneSelectedCategory]);

	useEffect(() => {
		if (activeSiteoneItems.length > 0 && filteredSiteoneCategories.length > 0) {
			setSiteoneSelectedCategory(filteredSiteoneCategories[0]);
		}
	}, [filteredSiteoneCategories]);

	useEffect(() => {
		if (recommendedSiteoneItems.length > 0) {
			setTimeout(() => {
				recommendedItemRef.current?.scrollIntoView({
					behavior: "smooth",
					block: "center"
				});
			}, 0);
		}
	}, [recommendedIndex, recommendedSiteoneItems]);

	let siteoneItemLoading = false;

	useEffect(() => {
		siteoneItemLoading = false
	}, [selectedSiteoneItem]);

	// match loading is used when toggling the link siteone item button, to prevent it from being toggled multiple times between item clicks
	let matchLoading = false;

	useEffect(() => {
		let cancelled = false;
		matchLoading = true;
		setIsSearchingLinkedSiteoneItem(true);
		const fetchLinkedSiteoneItem = async () => {
			if (!selectedItem || !siteoneCategoryQuery.data) {
				if (!cancelled) setIsSearchingLinkedSiteoneItem(false);
				return;
			}

			for (const category of siteoneCategoryQuery.data) {
				if (cancelled) return;
				const items = await getSiteoneInventoryItems(category.category_id);
				if (!items || items.length === 0) continue;
				let match = null
				for (const item of items) {
					if (item.linked_inventory_item_id !== null) {
						if (parseInt(item.linked_inventory_item_id) === parseInt(selectedItem.item_id)) {
							match = item;
							break;
						}

					}
				}
				if (match) {
					if (!cancelled) {
						setSelectedSiteoneItem(match)
						setSiteoneSelectedCategory({ category_id: match.line, category_name: match.line });
					}
					break;
				}

			}
			if (!cancelled) {
				matchLoading = false;
				setIsSearchingLinkedSiteoneItem(false);
			}
		};

		fetchLinkedSiteoneItem();
		return () => {
			cancelled = true;
		};
	}, [selectedItem, siteoneCategoryQuery.data]);



	const handleLinkSiteoneItemToInventoryItem = async () => {
		if (!selectedItem || !selectedSiteoneItem) return;

		try {
			const res = await saveSiteoneLinkedItem(selectedItem.item_id, selectedSiteoneItem.id);

			updateLinkedState(res);
			showSnackbar("SiteOne Item Linked Successfully");
		} catch {
			showSnackbar("Error Linking SiteOne Item");
		}
	};

	const handleChangeLinkSiteoneItemToInventoryItem = async () => {
		if (!selectedItem || !selectedSiteoneItem) return;

		try {
			const res = await changeSiteoneItemLink(selectedItem.item_id, selectedSiteoneItem.id);
			// Reset existing link state first (same as unlink)
			clearLinkState(res.fk_inventory_item_id, res.fk_siteone_inventory_item_id);
			updateLinkedState(res);
			showSnackbar("SiteOne Item Linked Successfully");
		} catch {
			showSnackbar("Error Linking SiteOne Item");
		}
	};

	const handleUnlinkSiteoneItemToInventoryItem = async () => {
		if (!selectedItem || !selectedSiteoneItem) return;

		try {
			await unlinkSiteoneItemLink(selectedItem.item_id, selectedSiteoneItem.id);
			clearLinkState(selectedItem.item_id, selectedSiteoneItem.id);
			showSnackbar("SiteOne Item Unlinked Successfully");
		} catch {
			showSnackbar("Error Unlinking SiteOne Item");
		}
	};

	const handleChangeCategory = (category: TInventoryCategory) => {
		setSelectedCategory(category);
		setSelectedItem(null);
		setSiteoneSelectedCategory(null);
		setSelectedSiteoneItem(null);
		setPrice("");
		setLeadTime("");
		setNotes("");
		// Clear search fields
		setSiteoneSiteoneItemSearch("");
		setSiteoneSiteoneItemNumberSearch("");
	};

	const handleSiteoneChangeCategory = (category: TInventoryCategory) => {
		setSiteoneSelectedCategory(category);
		setSelectedSiteoneItem(null);
	};

	const selectInventoryItem = (item: TInventory) => {
		setSelectedItem(item);
		setPrice(formatListPriceForInput(item.list_price));
	};

	const handleChangeItem = (item: TInventory) => {
		selectInventoryItem(item);

		// If item is already linked, clear search and let useEffect find the linked SiteOne item
		if (item.linked_inventory_item_id) {
			setSiteoneSiteoneItemSearch("");
			setSiteoneSiteoneItemNumberSearch("");
		} else {
			// Only auto-populate search if item is NOT linked
			if (siteoneItemNumberSearch) {
				setSiteoneSiteoneItemNumberSearch(item.model);
			} else {
				setSiteoneSiteoneItemSearch(item.item_name);
			}
		}
	};

	const handleSiteoneChangeItem = (item: TInventory) => {
		setSelectedSiteoneItem(item);
		
		// If the SiteOne item is linked to a local item, find and select that local item
		if (item.linked_inventory_item_id) {
			findLinkedLocalItem(item.linked_inventory_item_id);
		}
	};

	const findLinkedLocalItem = async (linkedInventoryItemId: string | number) => {
		setIsSearchingLinkedLocalItem(true);
		try {
			// First, try to find the item in already cached data
			const cachedItems = allItemsBySearch.data || [];
			const linkedLocalItem = cachedItems.find(
				(localItem: TInventory) => parseInt(localItem.item_id) === parseInt(String(linkedInventoryItemId))
			);

			if (linkedLocalItem) {
				// Find the category for this item
				const categories = categoryQuery.data || [];
				const itemCategory = categories.find(
					(cat: any) => cat.category_id === linkedLocalItem.category_id
				);
				
				if (itemCategory) {
					setSelectedCategory(itemCategory);
					selectInventoryItem(linkedLocalItem);
					setIsSearchingLinkedLocalItem(false);
					return;
				}
			}

			// If not found in cached data, make a single API call to get all items
			const allItems = await getInventoryItems(null, null, null);
			if (!allItems || allItems.length === 0) {
				setIsSearchingLinkedLocalItem(false);
				return;
			}

			// Find the local item that matches the linked_inventory_item_id
			const foundItem = allItems.find(
				(localItem: TInventory) => parseInt(localItem.item_id) === parseInt(String(linkedInventoryItemId))
			);

			if (foundItem) {
				// Find the category for this item
				const categories = await getInventoryCategories();
				const itemCategory = categories.find(
					(cat: any) => cat.category_id === foundItem.category_id
				);
				
				if (itemCategory) {
					setSelectedCategory(itemCategory);
					selectInventoryItem(foundItem);
				}
			}
		} catch (error) {
			console.error("Error finding linked local item:", error);
		} finally {
			setIsSearchingLinkedLocalItem(false);
		}
	};


	const updateLinkedState = (res: any) => {
		// update the item in the filteredItems list to have the linked_inventory_item_id
		const updatedItem = filteredItems.find((i: TInventory) => parseInt(i.item_id) === parseInt(res.fk_inventory_item_id));
		if (updatedItem) {
			updatedItem.linked_inventory_item_id = res.fk_siteone_inventory_item_id;
		}

		// update the item in the filteredSiteoneItems list to have the linked_inventory_item_id
		const updatedSiteoneItem = filteredSiteoneItems.find((i: TInventory) => parseInt(i.id as any) === parseInt(res.fk_siteone_inventory_item_id));
		if (updatedSiteoneItem) {
			updatedSiteoneItem.linked_inventory_item_id = res.fk_inventory_item_id;
		}
		setSelectedItem((prev: any) => ({
			...prev,
			linked_inventory_item_id: res.fk_siteone_inventory_item_id
		}));
		setSelectedSiteoneItem((prev: any) => ({
			...prev,
			linked_inventory_item_id: res.fk_inventory_item_id
		}));
	};


	const clearLinkState = (inventoryId: any, siteoneId: any) => {
		// update the item in the filteredItems list to have the linked_inventory_item_id set to null
		const updatedItem = filteredItems.find((i: TInventory) => parseInt(i.item_id) === parseInt(inventoryId));
		if (updatedItem) {
			updatedItem.linked_inventory_item_id = null;
		}

		// null the previously linked inventory item id
		const previouslyLinkedItem = filteredSiteoneItems.find((i: TInventory) => parseInt(i.linked_inventory_item_id as any) === parseInt(inventoryId));
		if (previouslyLinkedItem) {
			previouslyLinkedItem.linked_inventory_item_id = null;
		}

		// update the item in the filteredSiteoneItems list to have the linked_inventory_item_id set to null
		const updatedSiteoneItem = filteredSiteoneItems.find((i: TInventory) => parseInt(i.id as any) === parseInt(siteoneId));
		if (updatedSiteoneItem) {
			updatedSiteoneItem.linked_inventory_item_id = null;
		}

		setSelectedItem((prev: any) => ({
			...prev,
			linked_inventory_item_id: null
		}));
		setSelectedSiteoneItem((prev: any) => ({
			...prev,
			linked_inventory_item_id: null
		}));
	};

	const handleLinkItemSave = async () => {
		await axios.post(
			`${process.env.REACT_APP_SERVER_URL}/api/v1/company/vendors/addLinkedItem`,
			{
				vendorId: vendorId,
				itemId: selectedItem?.item_id,
				price: price,
				leadTime: normalizeLeadTimeStored(leadTime) || null,
				notes: clampVendorItemNotes(notes) || null,
			}).then(() => {
				setPrice("");
				setLeadTime("");
				setNotes("");
				setSelectedItem(null);
				queryClient.invalidateQueries(["vendors_items", vendorId]);
				categoryItemsQuery.refetch();
				linkCategoryItemsQuery.refetch();
				if (siteoneLocalItemSearch?.trim() || siteoneLocalItemNumberSearch?.trim()) {
					allItemsBySearch.refetch();
				}
				if (linkItemSearch?.trim()) {
					linkItemsBySearch.refetch();
				}
				showSnackbar(trAction("Item Linked Successfully", "Item Added Successfully"));
			}).catch(() => {
				showSnackbar(trAction("Error Linking Item", "Error Adding Item"));
			}
			);
	}

	const handleSiteOneLinkItemSave = async () => {
		await axios.post(
			`${process.env.REACT_APP_SERVER_URL}/api/v1/company/vendors/addLinkedSiteOneItem`,
			{
				vendorId: vendorId,
				siteOneItemId: selectedSiteoneItem?.id,
				price: selectedSiteoneItem?.your_price,
			}).then(async () => {
				// Immediately update the selected item to reflect it's been saved
				if (selectedSiteoneItem) {
					setSelectedSiteoneItem({
						...selectedSiteoneItem,
						is_in_vendor_item_details: true
					});
				}
				
				// Invalidate and refetch all SiteOne item queries to ensure fresh data from backend
				await queryClient.invalidateQueries(["siteone_vendor_link_items_all"]);
				await queryClient.invalidateQueries(["siteone_vendor_link_items_category"]);
				await queryClient.invalidateQueries(["vendor_link_items_search"]);
				
				// Refetch the currently active queries
				await Promise.all([
					allItemsBySearch.refetch(),
					allSiteoneItemsQuery.refetch(),
					siteoneCategoryItemsQuery.refetch()
				]);

				queryClient.invalidateQueries(["vendors_items", vendorId]);
				
				showSnackbar("SiteOne Item Added Successfully");
			}).catch(() => {
				showSnackbar("SiteOne Item Add Error");
			}
			);
	}

	const canLink =
		!matchLoading &&
		selectedItem &&
		selectedSiteoneItem &&
		!selectedItem.linked_inventory_item_id &&
		// parseInt(selectedItem.item_id) !== selectedSiteoneItem.linked_inventory_item_id &&
		selectedSiteoneItem.linked_inventory_item_id === null;

	const canChangeLink =
		selectedItem?.linked_inventory_item_id &&
		!selectedSiteoneItem?.linked_inventory_item_id &&
		parseInt(selectedItem.item_id) !== selectedSiteoneItem?.linked_inventory_item_id;

	const canUnlink =
		selectedItem?.linked_inventory_item_id &&
		selectedSiteoneItem?.linked_inventory_item_id &&
		parseInt(selectedItem.item_id) === selectedSiteoneItem.linked_inventory_item_id;

	if (categoryQuery?.isLoading) return <Spinner />;

	if (categoryQuery?.isError) return <div>Error</div>;

	if (isHeritageVendor) {
		return (
			<div className={styles.container}>
				{heritageHistoryItem && (
					<HeritageItemPurchaseHistoryModal
						heritagePartNumber={heritageHistoryItem.heritage_part_number}
						description={heritageHistoryItem.description}
						vendorId={vendorId}
						onClose={() => setHeritageHistoryItem(null)}
					/>
				)}
				{linkConfirmRequest && (
					<HeritageLinkConfirmModal
						request={linkConfirmRequest}
						isSubmitting={isHeritageLinkSubmitting}
						onClose={() => {
							if (!isHeritageLinkSubmitting) setLinkConfirmRequest(null);
						}}
						onConfirm={confirmHeritageLink}
					/>
				)}
				{unlinkConfirmRequest && (
					<HeritageUnlinkConfirmModal
						request={unlinkConfirmRequest}
						isSubmitting={isHeritageUnlinkSubmitting}
						onClose={() => {
							if (!isHeritageUnlinkSubmitting) setUnlinkConfirmRequest(null);
						}}
						onConfirm={confirmHeritageUnlink}
					/>
				)}
				<div className={styles.heritageLinkGrid}>
					<div className={styles.heritageLinkHeaderCell}>
						<h3 className={styles.heritageLinkPanelTitle}>Your Items</h3>
					</div>
					<div className={`${styles.heritageLinkHeaderCell} ${styles.heritageLinkHeaderCellRight}`}>
						<h3 className={styles.heritageLinkPanelTitle}>Heritage Items</h3>
					</div>

					<ClearableSearchInput
						placeholder="Search your items..."
						value={localSearch}
						onChange={(e) => setLocalSearch(e.target.value)}
						onClear={() => setLocalSearch("")}
					/>
					<div className="flex flex-wrap items-center gap-2">
						<div className="flex-1 min-w-0">
							<ClearableSearchInput
								placeholder="Search heritage parts..."
								value={search}
								onChange={(e) => setSearch(e.target.value)}
								onClear={() => setSearch("")}
							/>
						</div>
						<div className="w-64 shrink-0">
							<Select
								aria-label={tr("Heritage search mode")}
								isClearable={false}
								isSearchable={false}
								className="[&_.Select\_\_control]:!rounded-[30px]"
								value={selectedHeritageSearchOption}
								options={heritageSearchOptions}
								onChange={(selectedOption: any) => {
									const val = selectedOption?.value;
									if (val === "catalog_only") {
										setHeritagePurchasedOnly(false);
										setHeritageSearchAllOfHeritage(false);
									} else if (val === "purchased_only") {
										setHeritagePurchasedOnly(true);
										setHeritageSearchAllOfHeritage(false);
									} else {
										setHeritagePurchasedOnly(false);
										setHeritageSearchAllOfHeritage(true);
									}
									setHeritageItemsError(null);
								}}
							/>
						</div>
					</div>

					<div className={styles.heritageLinkListCell}>
						{selectedHeritageItemId != null && (
							<div className="sticky top-0 left-0 right-0 border-b bg-blue-50 text-blue-800 text-sm px-3 py-2 flex items-center justify-between z-10 shrink-0">
								<div className="truncate pr-2">
									Working on:{" "}
									<span className="font-medium">
										{userItems.find((i) => i.item_id === selectedHeritageItemId)?.item_name || "—"}
									</span>
								</div>
								<div className="flex items-center gap-2">
									<button
										type="button"
										className="text-xs text-blue-600 hover:underline"
										onClick={() => {
											const item = userItems.find(
												(i) => i.item_id === selectedHeritageItemId
											);
											const query = getHeritageSearchQueryForLocalItem(item);
											if (query) setSearch(query);
										}}
									>
										{tr("Search")}
									</button>

									<button
										type="button"
										className="text-xs text-blue-600 hover:underline"
										onClick={handleUnlink}
									>
										Unlink
									</button>

									<button
										type="button"
										className="text-xs text-blue-600 hover:underline"
										onClick={() => {
											setSelectedHeritageItemId(null);
											setSelectedHeritagePartNumber(null);
										}}
									>
										Clear
									</button>
								</div>
							</div>
						)}

						<div className="mt-2 flex-1 overflow-y-auto p-1 min-h-0">
							{userItems.map((item: VendorItemDetails) => (
								<div
									key={item.item_id}
									id={`local-link-item-${item.item_id}`}
									className={`border rounded p-2 cursor-pointer text-sm relative ${selectedHeritageItemId === item.item_id
										? "bg-blue-100 border-blue-600 ring-2 ring-blue-500"
										: "hover:bg-gray-50"
										}`}
									onClick={() => {
										if (selectedHeritageItemId === item.item_id) {
											setSelectedHeritageItemId(null);
											setSelectedHeritagePartNumber(null);
											return;
										}
										setSelectedHeritageItemId(item.item_id);
										setSelectedHeritagePartNumber(null);
									}}
									title={`Model: ${item.model || "N/A"}\nVendor Part #: ${item.vendor_part_number || "N/A"}\nSKU: ${item.sku || "N/A"}\nDesc: ${item.long_description?.slice(0, 100) || "N/A"}`}
								>
									<div className="font-semibold">{getItemDisplayName(item)}</div>
									<div className="text-xs text-gray-500">
										{item.model || "—"}{" "}
										<span className="text-[10px] uppercase tracking-wide text-gray-400 ml-1">Model</span>
									</div>
									<div className="text-xs text-gray-500">
										{item.sku || "—"}{" "}
										<span className="text-[10px] uppercase tracking-wide text-gray-400 ml-1">SKU</span>
									</div>
									{/* #21 — the mirror direction: this item exists locally but the
									    downloaded Heritage catalog has no counterpart for it. */}
									{localItemHasNoHeritageCounterpart(item) && (
										<span className="inline-block text-[10px] uppercase tracking-wide text-gray-500 bg-gray-100 border border-gray-200 rounded px-1.5 py-0.5 mt-1">
											{tr("Not in Heritage")}
										</span>
									)}
									{item.vendor_part_number && (
										<button
											type="button"
											className={`${styles.heritageLinkJumpButton} ${styles.heritageLinkJumpButtonCorner}`}
											title={tr("Show linked Heritage item")}
											aria-label={tr("Show linked Heritage item")}
											onClick={(e) => showLinkedHeritageItem(item, e)}
										>
											🔗
										</button>
									)}
								</div>
							))}
						</div>
					</div>

					<div className={styles.heritageLinkListCell}>
						{heritagePurchasedOnly && (
							<p className="text-xs text-gray-600 m-0 mt-1 shrink-0">
								{tr("Previously purchased items help")}
							</p>
						)}
						{heritageItemsError && (
							<p className="text-xs text-red-600 m-0 mt-1 shrink-0" role="alert">
								{heritageItemsError}
							</p>
						)}
						{/* #23 — linked/unlinked filter + the bulk bar when rows are selected. */}
						<div className="flex items-center gap-2 mt-1 shrink-0">
							<select
								className="text-xs border rounded px-1.5 py-1 bg-white"
								value={heritageLinkFilter}
								aria-label={tr("Filter linked state")}
								onChange={(e) =>
									setHeritageLinkFilter(e.target.value as "all" | "linked" | "unlinked")
								}
							>
								<option value="all">{tr("All items")}</option>
								<option value="linked">{tr("In your items only")}</option>
								<option value="unlinked">{tr("Not in your items only")}</option>
							</select>
							{bulkSelectedParts.size > 0 && (
								<button
									type="button"
									className="text-xs font-medium text-white bg-orange-600 hover:bg-orange-700 rounded px-2.5 py-1.5 touch-manipulation disabled:opacity-50"
									disabled={isBulkAdding}
									onClick={handleBulkAddAndLink}
								>
									{isBulkAdding
										? tr("Adding...")
										: tr("Add and link selected").replace(
												"{{count}}",
												String(bulkSelectedParts.size)
											)}
								</button>
							)}
						</div>
						{searchedBranches && searchedBranches.length > 0 && (
							<p className="text-[11px] text-gray-500 m-0 mt-1 shrink-0">
								{tr("Searched branches")}:{" "}
								{searchedBranches
									.map((b) => b.branch_name || b.branch_code)
									.filter(Boolean)
									.join(", ")}
							</p>
						)}
						{searchBranchErrors.length > 0 && (
							<p className="text-[11px] text-amber-700 m-0 mt-1 shrink-0" role="alert">
								{tr("Branches not searched")}:{" "}
								{searchBranchErrors.map((b) => b.branch_code).filter(Boolean).join(", ")}
							</p>
						)}
						<div className="mt-2 flex-1 overflow-y-auto pr-1 min-h-0">
							{heritageItemsLoading ? (
								<p className="text-sm text-gray-600 m-0">{tr("Loading heritage items...")}</p>
							) : heritageResults.length === 0 ? (
								<p className="text-sm text-gray-600 m-0">
									{heritagePurchasedOnly
										? tr("No previously purchased heritage items found")
										: debouncedSearch.trim().length < 2
											? tr("Heritage local catalog hint")
											: tr("No heritage items found")}
								</p>
							) : (
								buildHeritageSectionedList(filteredHeritageResults, isHeritageRowLinked).map((entry) => {
							// #21 — section headers split the pane into parts that have a
							// local counterpart (linked or server-matched) and parts the
							// company does not carry at all.
							if (entry.kind === "header") {
								return (
									<div
										key={`heritage-section-${entry.label}`}
										className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 bg-gray-100 border border-gray-200 rounded px-2 py-1 mb-2 mt-1"
									>
										{entry.label === "inYourItems"
											? tr("In your items")
											: tr("Not in your items")}{" "}
										({entry.count})
									</div>
								);
							}
							const item = entry.item;
							// #20 — server-decided linked-ness (shared predicate).
							const isLinked = isHeritageRowLinked(item);
							const isSelectedForLink = selectedHeritagePartNumber === item.heritage_part_number;
							return (
								<div
									key={item.heritage_part_number}
									id={`heritage-link-item-${item.heritage_part_number}`}
									className={`border rounded p-2 text-sm relative mb-2 flex gap-2 items-start ${isLinked
												? "bg-green-100 border-green-600 cursor-pointer"
												: isSelectedForLink
													? "bg-blue-100 border-blue-600 ring-2 ring-blue-500 cursor-pointer"
													: "hover:bg-gray-50 cursor-pointer"
									}`}
									onClick={() => {
										if (selectedHeritagePartNumber === item.heritage_part_number) {
											setSelectedHeritagePartNumber(null);
											return;
										}
										setSelectedHeritagePartNumber(item.heritage_part_number);
										handleLink(item.heritage_part_number);
									}}
								>
									{/*
										Heritage's own product image. Licensed for this screen only —
										it must not be copied onto the inventory item or shown for any
										other vendor, even for the same part.
									*/}
									{item.heritage_image_url && (
										<img
											src={item.heritage_image_url}
											alt=""
											loading="lazy"
											className="h-12 w-12 flex-shrink-0 rounded border border-gray-200 object-contain bg-white"
											onError={(e) => {
												// Heritage's CDN is outside our control; a broken link
												// should leave the row clean rather than show a broken image.
												e.currentTarget.style.display = "none";
											}}
										/>
									)}
									<div className="flex-1 min-w-0">
										<div className="font-semibold">{item.heritage_part_number}</div>
										<div className="text-xs text-gray-600">
											{item.description || "—"}{" "}
											<span className="text-[10px] uppercase tracking-wide text-gray-400 ml-1">
												Description
											</span>
										</div>
										{/*
											#32 — with several branches searched, say which of them
											returned the part; the same query genuinely differs per
											branch. Instant, unlike the live stock lookup below.
										*/}
										{searchedBranches &&
											searchedBranches.length > 1 &&
											(item.on_file_branches?.length ?? 0) > 0 && (
												<div className="text-[10px] text-gray-500 mt-0.5">
													{tr("On file at")}: {item.on_file_branches.join(", ")}
												</div>
											)}
										{(() => {
											/*
												#33 — live stock labelled by branch beats the cached
												number, whose branch nobody could see. Until (or unless)
												the live lookup lands, the cached value stays, marked as
												such.
											*/
											const live = branchStock[item.heritage_part_number];
											const readings = (live || []).filter((entry) => entry.qty != null);
											if (readings.length > 0) {
												return (
													<div className="text-xs text-gray-500 mt-1 flex flex-wrap items-baseline gap-x-1.5 gap-y-0.5">
														{readings.map((entry) => (
															<span
																key={entry.branch_code}
																className="inline-block bg-gray-100 border border-gray-200 rounded px-1.5 py-0.5 whitespace-nowrap"
															>
																{entry.branch_code} {String(entry.qty)}
															</span>
														))}
														<span className="text-[10px] uppercase tracking-wide text-gray-400">
															{tr("Stock at my branches")}
														</span>
													</div>
												);
											}
											if (live && !branchStockLoading) {
												return (
													<div className="text-xs text-gray-500 mt-1">
														—{" "}
														<span className="text-[10px] uppercase tracking-wide text-gray-400 ml-1">
															{tr("Stock at my branches")}
														</span>
													</div>
												);
											}
											return (
												<div className="text-xs text-gray-500 mt-1">
													{item.available_stock_level != null && item.available_stock_level !== ""
														? String(item.available_stock_level)
														: "—"}{" "}
													<span className="text-[10px] uppercase tracking-wide text-gray-400 ml-1">
														Avail. stock
													</span>
													{branchStockLoading && (
														<span className="text-[10px] text-gray-400 ml-2">
															{tr("Checking my branches...")}
														</span>
													)}
												</div>
											);
										})()}
									</div>

									<div className="flex flex-col items-end gap-1 shrink-0 self-start">
										<button
											type="button"
											className="text-xs font-medium text-blue-600 hover:underline min-h-[32px] px-2 touch-manipulation"
											onClick={(e) => {
												e.stopPropagation();
												setHeritageHistoryItem({
													heritage_part_number: item.heritage_part_number,
													description: item.description,
												});
											}}
										>
											{tr("History")}
										</button>
										{/* #22 — one action for a part the company does not carry:
										    create the local item and link it. Only offered on rows in
										    "Not in your items" (same predicate as the section split). */}
										{!isLinked && item.matched_item_id == null && (
											<div className="flex items-center gap-1">
												{/* #23 — multi-select for one bulk Add and link. */}
												<input
													type="checkbox"
													className="h-4 w-4 shrink-0 touch-manipulation"
													aria-label={`${tr("Select for bulk add")} ${item.heritage_part_number}`}
													checked={bulkSelectedParts.has(item.heritage_part_number)}
													disabled={isBulkAdding}
													onClick={(e) => e.stopPropagation()}
													onChange={(e) => {
														e.stopPropagation();
														setBulkSelectedParts((prev) => {
															const next = new Set(prev);
															if (next.has(item.heritage_part_number)) {
																next.delete(item.heritage_part_number);
															} else {
																next.add(item.heritage_part_number);
															}
															return next;
														});
													}}
												/>
												<button
													type="button"
													className="text-xs font-medium text-orange-600 hover:underline min-h-[32px] px-2 touch-manipulation disabled:opacity-50 disabled:no-underline"
													disabled={addingHeritageParts.has(item.heritage_part_number) || isBulkAdding}
													onClick={(e) => handleAddAndLink(item, e)}
												>
													{addingHeritageParts.has(item.heritage_part_number)
														? tr("Adding...")
														: tr("Add and link")}
												</button>
											</div>
										)}
										{isLinked && (
											<button
												type="button"
												className={styles.heritageLinkJumpButton}
												title={tr("Show linked local item")}
												aria-label={tr("Show linked local item")}
												onClick={(e) =>
													showLinkedLocalItem(item.heritage_part_number, e)
												}
											>
												🔗
											</button>
										)}
									</div>
								</div>
							);
						})
						)}
					</div>
					</div>
				</div>
			</div>
		);
	}


	if (isSiteoneVendor) {
		return (
			<div className={styles.container}>
				<div className={styles.pickersContainer}>

					<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
						<h2>Local Items</h2>
						{/* button that toggles between item and item number search */}
						<Button
							style={{ marginLeft: "auto", marginRight: "5px" }}
							onClick={() => {
								setLocalItemNumberSearch(!localItemNumberSearch)
								setSiteoneLocalItemNumberSearch("")
								setSiteoneLocalItemSearch("")
							}}
							disabled={!siteoneCategoryQuery.data || siteoneCategoryQuery.data.length === 0}
						>
							{localItemNumberSearch ? tr("Search by Item Name") : tr("Search by Item Number")}
						</Button>

						{
							localItemNumberSearch ? (
								<SearchInput
									search
									placeholder={tr("Search by Number")}
									value={siteoneLocalItemNumberSearch}
									onChange={(e) => setSiteoneLocalItemNumberSearch((e.target as HTMLInputElement).value)}
								/>
							) : (
								<SearchInput
									search
									placeholder={tr("Search by Name")}
									value={siteoneLocalItemSearch}
									onChange={(e) => setSiteoneLocalItemSearch((e.target as HTMLInputElement).value)}
								/>
							)
						}
					</div>

					<div className={styles.pickers}>
						{/* Category column */}
						<div className={styles.categoryList}>
							{/* if categoryQuery category_id is not in the itemsQuery, do not show it */}
							{(filteredCategories).map((category: any) => (
								<div
									key={category.category_id}
									className={
										selectedCategory?.category_id === category.category_id
											? styles.selectedCategoryContainer
											: styles.categoryContainer
									}
									onClick={() => handleChangeCategory(category)}
								>
									{/* if every item in this category is linked, show a linked icon */}
									{filteredItems?.length > 0 && filteredItems.every((item: TInventory) => item.linked_inventory_item_id && selectedCategory?.category_id === item.category_id) ? (
										<span className={styles.linkedIcon}>🔗</span>
									) : null}

									{category.category_name}
								</div>
							))}

						</div>

						{/* Filtered item list */}
						<div className={styles.itemList}>
							{
								// Show spinner when loading
								(filteredItems?.length === 0 && selectedCategory && (allItemsBySearch.isLoading || categoryItemsQuery.isLoading)) ? (
									<Spinner />
								) : 
								// Show no items found when search is active but no results
								(filteredItems?.length === 0 && selectedCategory && (siteoneLocalItemSearch?.trim() || siteoneLocalItemNumberSearch?.trim()) && !allItemsBySearch.isLoading && !categoryItemsQuery.isLoading) ? (
									<div className="text-center text-lg mt-2 font-bold">
										{tr("No items found")}
									</div>
								) : 
								// Show no category selected message
								!selectedCategory ? (
									<div className={styles.noCategorySelected}>
										{tr("Select a Category to view items")}
									</div>
								) : null
							}
							{filteredItems?.map((item: TInventory) => (
								<div
									key={item.item_id}
									className={
										selectedItem?.item_id === item.item_id
											? styles.selectedItemContainer
											: styles.itemContainer
									}
									onClick={() => handleChangeItem(item)}
								>
									{item.item_name}
									<div style={{ display: "block", fontSize: "12px", color: "#666", marginLeft: "auto", marginRight: "0", width: "fit-content" }}>
										{item.model}
									</div>
									{
										item.linked_inventory_item_id
											? (
												<span className={styles.linkedIcon}>🔗</span>
											) : null
									}
								</div>
							))}
						</div>
					</div>
				</div>

				<div style={{ width: "33%", margin: "20px" }}>
					{/* find best match between siteoneSelectedCategory and selectedCategory */}

					{/* <Button
						style={{ marginTop: "10px" }}
						onClick={async () => {
							if (!selectedItem || !siteoneCategoryQuery.data) return;

							let bestMatches: { item: TInventory; score: number }[] = [];
							let bestCategory: TInventoryCategory | null = null;

							// Group all SiteOne items by category
							const itemsGroupedByCategory = new Map<number, TInventory[]>();

							if (allSiteoneItemsQuery.data?.length > 0) {
								allSiteoneItemsQuery.data.forEach((item: TInventory) => {
									if (!itemsGroupedByCategory.has(item.line as any)) {
										itemsGroupedByCategory.set(item.line as any, []);
									}
									itemsGroupedByCategory.get(item.line as any)!.push(item);
								});
							} else {
								// Fetch items for all categories manually
								const results = await Promise.all(
									siteoneCategoryQuery.data.map(async (category: TInventoryCategory) => {
										const items = await getSiteoneInventoryItems(category.category_id);
										return { category, items };
									})
								);

								results.forEach(({ category, items }) => {
									itemsGroupedByCategory.set(category.category_id, items);
								});
							}

							// Match scoring per category
							const allResults = Array.from(itemsGroupedByCategory.entries()).map(([catId, items]) => {
								const matches = findTopMatches(selectedItem, items);
								const category = siteoneCategoryQuery.data.find((c: any) => c.category_id === catId) || null;
								return { category, matches };
							});

							const sorted = allResults
								.filter((entry) => entry.matches.length > 0)
								.sort((a, b) => b.matches[0].score - a.matches[0].score);

							if (sorted.length > 0) {
								bestMatches = sorted[0].matches;
								bestCategory = sorted[0].category;
							}

							if (bestMatches.length > 0 && bestCategory) {
								setRecommendedSiteoneItems(bestMatches);
								setRecommendedIndex(0);
								setRecommendedSiteoneCategory(bestCategory);
								setSelectedSiteoneItem(bestMatches[0] as any);
								setSiteoneSelectedCategory(bestCategory);
								showSnackbar(`Found ${bestMatches.length} matching items`);
							} else {
								showSnackbar("No suitable SiteOne matches found.");
							}
						}}
						disabled={!selectedItem || !siteoneCategoryQuery.data}
					>
						Find Closest SiteOne Match
					</Button> */}

					<div style={{ display: "flex", gap: "10px" }}>

						{
							canLink ? (
								<Button
									onClick={handleLinkSiteoneItemToInventoryItem}
									primary
								// disabled={isLinkDisabled}
								>
									Link SiteOne Item
								</Button>
							) : canChangeLink ? (
								<Button
									onClick={handleChangeLinkSiteoneItemToInventoryItem}
									primary
								>
									Change Linked Item
								</Button>
							) : canUnlink ? (
								<Button
									onClick={handleUnlinkSiteoneItemToInventoryItem}
									primary
								>
									Unlink SiteOne Item
								</Button>
							) : (
								<Button disabled>Link SiteOne Item</Button>
							)
						}

						{/* if item is linked to siteone item, show linked label */}
						{isSearchingLinkedSiteoneItem ? (
							<div style={{ color: "blue", marginTop: "10px", display: "flex", alignItems: "center", gap: "8px" }}>
								<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
								Searching for linked SiteOne item...
							</div>
						) : selectedSiteoneItem && selectedSiteoneItem.linked_inventory_item_id === selectedItem?.item_id || selectedItem?.linked_inventory_item_id ? (
							<div style={{ color: "green", marginTop: "10px" }}>
								Already Linked!
							</div>
						) : null}

					</div>

					{recommendedSiteoneItems.length > 1 && (
						<div style={{ marginTop: "10px", display: "flex", gap: "10px" }}>
							<Button
								disabled={recommendedIndex <= 0}
								onClick={() => {
									const newIndex = recommendedIndex - 1;
									setRecommendedIndex(newIndex);
									setSelectedSiteoneItem(recommendedSiteoneItems[newIndex] as any);
								}}
							>
								Back
							</Button>
							<Button
								disabled={recommendedIndex >= recommendedSiteoneItems.length - 1}
								onClick={() => {
									const newIndex = recommendedIndex + 1;
									setRecommendedIndex(newIndex);
									setSelectedSiteoneItem(recommendedSiteoneItems[newIndex] as any);
								}}
							>
								Next
							</Button>
						</div>
					)}

					<div
						style={{
							display: "flex",
							flexDirection: "row",
							gap: "10px",
							marginTop: "30px",
							alignItems: "flex-start",
						}}
					>
						{/* Local Item Column */}
						<div style={{ display: "flex", flexDirection: "column", gap: "12px", flex: 1 }}>
							<label style={{ fontWeight: 600 }}>Local Category</label>
							<input
								type="text"
								value={selectedCategory?.category_name || ""}
								readOnly
								style={{
									padding: "8px",
									border: "1px solid #ccc",
									borderRadius: "4px",
									background: "#f9f9f9",
								}}
							/>

							<label style={{ fontWeight: 600 }}>Local Item</label>
							<input
								type="text"
								value={selectedItem?.item_name || ""}
								readOnly
								style={{
									padding: "8px",
									border: "1px solid #ccc",
									borderRadius: "4px",
									background: "#f9f9f9",
								}}
							/>
							
							<label style={{ fontWeight: 600 }}>Local Item Number</label>
							<input
								type="text"
								value={selectedItem?.model || ""}
								readOnly
								style={{
									padding: "8px",
									border: "1px solid #ccc",
									borderRadius: "4px",
									background: "#f9f9f9",
								}}
							/>
						</div>

						{/* SiteOne Item Column */}
						<div style={{ display: "flex", flexDirection: "column", gap: "12px", flex: 1 }}>
							<label style={{ fontWeight: 600 }}>SiteOne Category</label>
							<input
								type="text"
								value={siteoneSelectedCategory?.category_name || ""}
								readOnly
								style={{
									padding: "8px",
									border: "1px solid #ccc",
									borderRadius: "4px",
									background: "#f0f0f0",
								}}
							/>

							<label style={{ fontWeight: 600 }}>SiteOne Item</label>
							<input
								type="text"
								value={selectedSiteoneItem?.description || ""}
								readOnly
								style={{
									padding: "8px",
									border: "1px solid #ccc",
									borderRadius: "4px",
									background: "#f0f0f0",
								}}
							/>

							<label style={{ fontWeight: 600 }}>SiteOne Item Number</label>
							<input
								type="text"
								value={selectedSiteoneItem?.item_number || ""}
								readOnly
								style={{
									padding: "8px",
									border: "1px solid #ccc",
									borderRadius: "4px",
									background: "#f0f0f0",
								}}
							/>
						</div>
					</div>


					{selectedSiteoneItem ? (
						<>
							<div className={styles.itemDetailsContainer}>
								<>
									<img
										alt={selectedSiteoneItem.item_name}
										src={selectedItem?.imageUrl ? selectedItem.imageUrl : NoProductImage}
										className={styles.image}
									/>

									<div className={styles.detailGroup}>
										<div className={styles.itemName}>{selectedSiteoneItem.item_name}</div>
										<div className={styles.description}>{selectedSiteoneItem.description}</div>
									</div>

								</>
							</div>
							<div className={styles.formContainer}>
								{/* <Field
								advancedNumericalInputHandling
								value={selectedSiteoneItem?.your_price || ""}
								// setValue={setPrice}
								placeholder={tr("Price")}
								style={{ marginBottom: "10px" }}
								// disable this
								readOnly
							/> */}
								<label style={{ fontWeight: 600, marginBottom: "10px" }}>
									Price
								</label>
								<Input
									style={{ marginBottom: "10px", backgroundColor: "#f0f0f0" }}
									placeholder={tr("Price")}
									value={selectedSiteoneItem?.your_price || ""}
									readOnly
								/>
								{/* <Input
								style={{ marginBottom: "10px" }}
								placeholder={tr("Lead Time")}
								value={leadTime}
								onChange={(e: any) => setLeadTime(e.target.value)}
							/>
							<Input
								style={{ marginBottom: "10px" }}
								placeholder={tr("Notes")}
								value={notes}
								onChange={(e: any) => setNotes(e.target.value)}
							/> */}
								<div style={{ marginTop: "20px" }}>
									{selectedSiteoneItem?.is_in_vendor_item_details ? (
										<div style={{
											backgroundColor: "#f0fdf4",
											border: "1px solid #86efac",
											borderRadius: "8px",
											padding: "12px 16px",
											display: "flex",
											alignItems: "center",
											gap: "10px",
											marginBottom: "12px"
										}}>
											<svg 
												width="20" 
												height="20" 
												viewBox="0 0 20 20" 
												fill="none" 
												xmlns="http://www.w3.org/2000/svg"
												style={{ flexShrink: 0 }}
											>
												<path 
													d="M10 0C4.48 0 0 4.48 0 10C0 15.52 4.48 20 10 20C15.52 20 20 15.52 20 10C20 4.48 15.52 0 10 0ZM8 15L3 10L4.41 8.59L8 12.17L15.59 4.58L17 6L8 15Z" 
													fill="#16a34a"
												/>
											</svg>
											<div style={{ flex: 1 }}>
												<div style={{ 
													color: "#15803d", 
													fontWeight: 600,
													fontSize: "14px",
													marginBottom: "2px"
												}}>
													Already Saved
												</div>
												<div style={{ 
													color: "#166534", 
													fontSize: "13px"
												}}>
													This item is already saved to vendor items
												</div>
											</div>
										</div>
									) : null}
									
									<Button
										className={styles.saveButton}
										onClick={handleSiteOneLinkItemSave}
										primary
										disabled={!selectedSiteoneItem || selectedSiteoneItem?.is_in_vendor_item_details}
										style={{ width: "100%" }}
									>
										{selectedSiteoneItem?.is_in_vendor_item_details 
											? "Already Saved to Vendor Items" 
											: tr("Save")
										}
									</Button>
								</div>
							</div>

						</>
					) : null}


				</div>

				<div className={styles.pickersContainer}>

					<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
						<h2>SiteOne Items</h2>
						{/* button that toggles between item and item number search */}
						<Button
							style={{ marginLeft: "auto", marginRight: "5px" }}
							onClick={() => {
								const newMode = !siteoneItemNumberSearch;
								setSiteoneItemNumberSearch(newMode);
								
								// If there's a selected LOCAL item
								if (selectedItem) {
									// If item is already linked, clear search (let useEffect find linked item)
									if (selectedItem.linked_inventory_item_id) {
										setSiteoneSiteoneItemNumberSearch("");
										setSiteoneSiteoneItemSearch("");
									} else {
										// Only populate search if item is NOT linked
										if (newMode) {
											// Switching to number search - use local item's model
											setSiteoneSiteoneItemNumberSearch(selectedItem.model || "");
											setSiteoneSiteoneItemSearch("");
										} else {
											// Switching to name search - use local item's name
											setSiteoneSiteoneItemSearch(selectedItem.item_name || "");
											setSiteoneSiteoneItemNumberSearch("");
										}
									}
								} else {
									setSiteoneSiteoneItemNumberSearch("");
									setSiteoneSiteoneItemSearch("");
								}
							}}
							disabled={!siteoneCategoryQuery.data || siteoneCategoryQuery.data.length === 0}
						>
							{siteoneItemNumberSearch ? tr("Search by Item Name") : tr("Search by Item Number")}
						</Button>

						{
							siteoneItemNumberSearch ? (
								<SearchInput
									search
									placeholder={tr("Search by Number")}
									value={siteoneSiteoneItemNumberSearch}
									onChange={(e) => setSiteoneSiteoneItemNumberSearch((e.target as HTMLInputElement).value)}
								/>
							) : (
								<SearchInput
									search
									placeholder={tr("Search by Name")}
									value={siteoneSiteoneItemSearch}
									onChange={(e) => setSiteoneSiteoneItemSearch((e.target as HTMLInputElement).value)}
								/>
							)
						}
					</div>

					<div className={styles.pickers}>
						<div className={styles.categoryList}>
							{filteredSiteoneCategories?.map((category: TInventoryCategory) => {
								const isSelected = siteoneSelectedCategory?.category_id === category.category_id;
								const isRecommended = recommendedSiteoneCategory?.category_id === category.category_id;

								return (
									<div
										className={
											isSelected
												? styles.selectedCategoryContainer
												: isRecommended
													? styles.recommendedCategoryContainer
													: styles.categoryContainer
										}
										onClick={() => handleSiteoneChangeCategory(category)}
									>
										{category.category_name}
									</div>
								);
							})}
						</div>

						<div className={styles.itemList}>
							{
								// Show spinner when loading
								(filteredSiteoneItems?.length === 0 && siteoneSelectedCategory && (allSiteoneItemsQuery.isLoading || siteoneCategoryItemsQuery.isLoading)) ? (
									<Spinner />
								) : 
								// Show no items found when search is active but no results
								(filteredSiteoneItems?.length === 0 && siteoneSelectedCategory && (siteoneSiteoneItemSearch?.trim() || siteoneSiteoneItemNumberSearch?.trim()) && !allSiteoneItemsQuery.isLoading && !siteoneCategoryItemsQuery.isLoading) ? (
									<div className="text-center text-lg mt-2 font-bold">
										{tr("No items found")}
									</div>
								) : 
								// Show no category selected message
								!siteoneSelectedCategory ? (
									<div className={styles.noCategorySelected}>
										{tr("Select a Category to view items")}
									</div>
								) : null
							}

							{/* Recommended items */}
							{filteredSiteoneItems?.map((item: TInventory) => {
								const recommended = recommendedSiteoneItems?.find(r => r.item.id === item.id);
								const score = recommended?.score ?? null;
								const maxScore = recommendedSiteoneItems.length > 0 ? recommendedSiteoneItems[0].score : 1;

								const backgroundColor = score ? getGreenShade(score, maxScore) : undefined;

								return (
									<div
										ref={item.id === currentRecommendedItem?.id ? recommendedItemRef : null}
										className={
											item.id === currentRecommendedItem?.id
												? styles.recommendedItemContainer
												: item.id === selectedSiteoneItem?.id
													? styles.selectedItemContainer
													: styles.itemContainer
										}
										style={score ? { backgroundColor } : {}}
										onClick={() => handleSiteoneChangeItem(item)}
									>
										{item.description}
										<div style={{ display: "block", fontSize: "12px", color: "#666", marginLeft: "auto", marginRight: "0", width: "fit-content" }}>
											{item.item_number}
										</div>

										{/* if already linked, show linked icon */}
										{
											item.linked_inventory_item_id
												? (
													<span className={styles.linkedIcon}>🔗</span>
												) : null
										}
									</div>
								);
							})}
						</div>

					</div>
				</div>


			</div>
		)
	}

	// return  <div></div>


	return (
		<div className={styles.container}>
			<div className={styles.pickersContainer}>
				<SearchInput
					search
					placeholder={tr("Search by Name")}
					value={linkItemSearch}
					onChange={(e) => setLinkItemSearch((e.target as HTMLInputElement).value)}
					style={{ width: "100%", marginBottom: "10px" }}
				/>

				<div className={styles.pickers}>
					<div className={styles.categoryList}>
						{linkFilteredCategories?.map((category: TInventoryCategory) => (
							<div
								key={category.category_id}
								className={
								selectedCategory && category.category_id === selectedCategory.category_id
									? styles.selectedCategoryContainer
									: styles.categoryContainer
							}
							onClick={() => handleChangeCategory(category)}
						>
							{category.category_name}
						</div>
					))}
				</div>
				<div className={styles.itemList}>
						{linkFilteredItems?.length === 0 && selectedCategory && (linkItemsBySearch.isLoading || linkCategoryItemsQuery.isLoading) ? (
						<Spinner />
					) : null}
						{linkCategoryItemsQuery?.isError || linkItemsBySearch?.isError ? <div>{tr("Error")}</div> : null}
						{linkItemSearch?.trim() && linkFilteredCategories.length === 0 && !linkItemsBySearch.isLoading ? (
							<div className={styles.noCategorySelected}>
								{tr("No items found")}
							</div>
						) : null}
						{!linkItemSearch?.trim() && !selectedCategory ? (
							<div className={styles.noCategorySelected}>
								{trAction("Select a Category to view items", "Choose a Category")}
							</div>
						) : null}
						{linkItemSearch?.trim() && selectedCategory && linkFilteredItems.length === 0 && !linkItemsBySearch.isLoading ? (
							<div className={styles.noCategorySelected}>
								{tr("No items found")}
							</div>
						) : null}
						{linkFilteredItems?.map((item: TInventory) => (
						<div
							key={item.item_id}
							className={
								selectedItem && item.item_id === selectedItem.item_id
									? styles.selectedItemContainer
									: styles.itemContainer
							}
							onClick={() => handleChangeItem(item)}
						>
							{item.item_name}
						</div>
						))}
					</div>
				</div>
			</div>

			<div style={{ margin: "20px", width: "50%" }}>

				{selectedItem ? (
					<>
						<div className={styles.itemDetailsContainer}>
							<>
								<img
									alt={selectedItem.item_name}
									src={selectedItem?.imageUrl ? selectedItem.imageUrl : NoProductImage}
									className={styles.image}
								/>

								<div className={styles.detailGroup}>
									<div className={styles.itemName}>{selectedItem.item_name}</div>
									<div className={styles.description}>{selectedItem.long_description}</div>
								</div>

							</>
						</div>
						
						{isIntegratedVendor && isSearchingLinkedLocalItem && (
							<div style={{ color: "blue", marginTop: "10px", display: "flex", alignItems: "center", gap: "8px" }}>
								<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
								Searching for linked local item...
							</div>
						)}
						<div className={styles.formContainer}>
							<VendorItemPriceInput
								value={price}
								onChange={setPrice}
								placeholder={tr("Price")}
								className="border px-2 py-1 rounded text-sm w-full h-[40px] min-h-[40px] max-h-[40px] box-border mb-2"
							/>
							<label style={{ fontWeight: 600, marginBottom: "4px", display: "block" }}>
								{tr("Lead Time")}
							</label>
							<div className="mb-2">
								<VendorLeadTimeInput
									value={leadTime}
									onChange={setLeadTime}
								/>
							</div>
							<label style={{ fontWeight: 600, marginBottom: "4px", display: "block" }}>
								{tr("Notes")}
							</label>
							<VendorItemNotesInput
								value={notes}
								onChange={setNotes}
								className="border px-2 py-1 rounded text-sm w-full h-[40px] min-h-[40px] max-h-[40px] box-border mb-2"
							/>
							<div className={styles.footerRow}>
								<Button
									className={styles.saveButton}
									onClick={handleLinkItemSave}
									primary
									disabled={!price}
								>
									{tr("Save")}
								</Button>
							</div>
						</div>

					</>
				) : null}
			</div>

		</div>
	);

};

export default VendorItemLinkModalBody;
